import {
  Divider,
  DropdownButton,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import {
  DashboardLayout,
  QueueDashboardQueueSortBy,
} from 'lib-dashboard-types';
import { ampli } from 'lib-shared-tracking';
import { Fragment, memo, useMemo, useState } from 'react';

import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';

import {
  layoutPreferenceAssets,
  queueSortByPreferenceAssets,
} from '../../../../../../assets/dashboardPreferencesAssets';
import { useQueueDashboardFavorite } from '../../../../utils/useQueueDashboardFavorite';

import styles from './queueDashboardPreferencesControl.module.scss';

export const QueueDashboardPreferencesControl = memo(
  function QueueDashboardPreferencesControl(): JSX.Element {
    const { params, patchParams, unsetParams } =
      useAppSearchParams('/dashboard/queues');
    const { favorite } = useQueueDashboardFavorite();

    const storage = useServerStorage('user');
    const currentLayout = storage.data.dashboard?.layout ?? 'default';

    const [isOpen, setIsOpen] = useState(false);

    const handleLayoutChange = (layout: DashboardLayout) => {
      storage.set({
        dashboard: (draft) => {
          draft.layout = layout;
        },
      });

      ampli.dashboardLayoutChanged({ layout });
    };

    const handleSortByChange = (value: QueueDashboardQueueSortBy) => {
      if (value === params.sort_by) {
        unsetParams(['sort_by', 'page']);
        ampli.dashboardQueuesSortingUpdated({ type: undefined });
      } else {
        patchParams({
          set: {
            sort_by: value,
          },
          unset: ['page'],
        });

        ampli.dashboardQueuesSortingUpdated({ type: value });
      }
    };

    const currentSort = useMemo((): QueueDashboardQueueSortBy | undefined => {
      if (params.sort_by) return params.sort_by;

      // If no sort is explicitly set in the URL, and in `custom` mode, we don't apply any
      // sorting and instead rely on the order of the `queue_ids` from the URL.
      if (params.type === 'custom') return undefined;

      // If viewing a favorite, use the favorite's `sort_by` if it has one configured, otherwise
      // use the order of the queues in the favorite.
      if (params.type === 'favourite' && params.id) {
        return favorite?.params.sort_by;
      }

      // The default sorting.
      return 'name:asc';
    }, [favorite?.params.sort_by, params.id, params.sort_by, params.type]);

    const selectedValues: [
      QueueDashboardQueueSortBy | undefined,
      DashboardLayout,
    ] = useMemo(
      () => [currentSort, currentLayout],
      [currentSort, currentLayout],
    );

    return (
      <DropdownButton
        show={isOpen}
        onToggle={setIsOpen}
        icon="preferences"
        buttonProps={{
          variant: 'secondary',
          disabled: !!params.q,
        }}
        fixed
        dropdown={
          <>
            <SelectionList
              className={styles.list}
              isActive={false}
              selectedValues={selectedValues}
              defaultActiveItemIndex={0}
            >
              <Divider padding="300 300 200">
                <Text.Paragraph as="span" color="tertiary">
                  Sort by
                </Text.Paragraph>
              </Divider>

              {queueSortByPreferenceAssets.map(({ value, label, image }) => (
                <Fragment key={value}>
                  <SelectionList.Item
                    value={value}
                    contentClassName={styles.item}
                    onClick={() => handleSortByChange(value)}
                  >
                    {label} {image}
                  </SelectionList.Item>
                </Fragment>
              ))}

              <Divider padding="200 300">
                <Text.Paragraph as="span" color="tertiary">
                  Layout
                </Text.Paragraph>
              </Divider>

              {layoutPreferenceAssets.map(({ value, label, image }) => (
                <Fragment key={value}>
                  <SelectionList.Item
                    value={value}
                    contentClassName={styles.item}
                    onClick={() => handleLayoutChange(value)}
                  >
                    {label} {image}
                  </SelectionList.Item>
                </Fragment>
              ))}
            </SelectionList>
          </>
        }
      />
    );
  },
);
